import { ListenerFn } from 'eventemitter2';
import type { IAssetsLoader } from '../../AssetLoader/IAssetsLoader';
import { ElementTypes } from '../../Enums/ElementTypes';
import { DynamicEventEmitter } from '../../Helpers/DynamicEventEmitter';
import { exportSpecificationTemplate } from '../../SpecificationParser/SpecificationExporter';
import { BaseModel } from './BaseModel';
import {
    type AudioElementParams,
    type ContentPropertiesSettings,
    type ControllableElement,
    type CreateElementOptions,
    type Element,
    type ElementForContentProperty,
    type ElementUpdateOptions,
    type ModelMetadata,
    type MultiPageModelSnapshot,
    type PageDuplicateParams,
    type Reason,
    type RemoveElementOptions,
    type TranscriptElementParams,
    type UpdateElementOptions,
    type UpdateGlobalPropertyOptions,
} from '../../types';
import { Dimension } from '../Shared/Dimension';
import { ElementUpdatedEventData, EventDataMap, TextStyleChangeEventData } from '../../event-types';
import { GroupElement } from '../Elements/GroupElement';
import { BackgroundColor, BackgroundColorParams, defaultBackgroundColorParams } from '../Properties/BackgroundColor';
import { ElementUpdateTypes } from '../../Enums/ElementUpdateTypes';
import { TextElement } from '../Elements/TextElement';
import { ShapeElement } from '../Elements/ShapeElement';
import { Ranges } from '../../Helpers/Ranges';
import { MaskModeTypes } from '../../Enums/MaskModeTypes';
import { TextStyles } from '../Shared/TextStyles';
import { BaseProperty } from '../Properties/BaseProperty';
import { type ContentPropertiesManager } from '../../Helpers/ContentPropertiesManager';
import { matchElementTypeToContentPropertySetting } from '../../Helpers/elementUtils';
import { deepClone, detectCratedElements, updateListOrderByIdxs } from '../../Helpers/utils';
import { IAsset } from '../Assets/IAsset';
import { type EventAccumulator } from '../Shared/EventAccumulator';

const MAX_TEXT_FONT_SCALE = 10;
const MAX_TEXT_BACKGROUND_CORNER_RADIUS = 100;
const MAX_SHAPE_BORDER_RADIUS = 999;

export abstract class BaseMultiPageModel<M extends BaseModel = BaseModel> extends BaseModel {
    eventEmitter: DynamicEventEmitter = new DynamicEventEmitter();

    protected index = 0;

    protected listeners: ListenerFn[] = [];

    protected models: M[] = [];

    protected modelsMetaData: ModelMetadata[] = [];

    protected contentPropertiesManager!: ContentPropertiesManager;

    createModel(): M {
        throw new Error('Method not implemented.');
    }

    setAssetLoader(assetLoader: IAssetsLoader) {
        this.assetLoader = assetLoader;
        this.models.forEach((model) => {
            model.setAssetLoader(assetLoader);
        });

        return this;
    }

    getAssetLoader(): IAssetsLoader {
        return this.assetLoader;
    }

    setTextStyles(textStyles: TextStyles) {
        this.textStyles = textStyles;
        textStyles.setEventEmitter(this.eventEmitter);

        this.models.forEach((model) => {
            model.setTextStyles(textStyles);
        });

        return this;
    }

    setContentPropertiesManager(contentPropertiesManager: ContentPropertiesManager) {
        this.contentPropertiesManager = contentPropertiesManager;
        this.contentPropertiesManager.setEventEmitter(this.eventEmitter);
        this.contentPropertiesManager.setMultiPageModel(this);

        return this;
    }

    getTextStyles(): TextStyles {
        return this.textStyles;
    }

    getContentPropertiesManager(): ContentPropertiesManager {
        return this.contentPropertiesManager;
    }

    setup() {
        this.models.forEach((model, index) => {
            model.setup();
            this.subscribeToModelEvents(model, index, this.modelsMetaData[index].id);
        });

        this.subscribe();

        return this;
    }

    getEventEmitter(): DynamicEventEmitter {
        return this.eventEmitter;
    }

    getCurrentPageIndex(): number {
        return this.index;
    }

    setCurrentPageIndex(index: number) {
        if (this.index === index) {
            return this;
        }

        this.eventEmitter.emit('requestToChangeCurrentPage');
        this.index = index;
        this.eventEmitter.emit('currentPageChange', index);

        return this;
    }

    getCurrentPageId() {
        return this.modelsMetaData[this.index].id;
    }

    getCurrentModel() {
        return this.models[this.index];
    }

    getModelByPageId(pageId: string) {
        const modelIdx = this.modelsMetaData.findIndex((modelMetaData) => modelMetaData.id === pageId);

        if (modelIdx !== -1) {
            return this.models[modelIdx];
        }
    }

    getModels() {
        return this.models;
    }

    setModels(models: M[]) {
        this.models = models;

        return this;
    }

    getModelsMetaData() {
        return this.modelsMetaData;
    }

    setModelsMetaData(modelsMetaData: ModelMetadata[], pageMap?: Map<string, string>) {
        this.modelsMetaData = modelsMetaData;
        this.eventEmitter.emit('currentPageChange', this.index);

        // Update all events after changing ids – happens only once after saving
        if (pageMap) {
            this.unsubscribeFromAllModelEvents();

            this.models.forEach((model, index) => {
                this.subscribeToModelEvents(model, index, this.modelsMetaData[index].id);
            });
        }

        this.eventEmitter.emit('pagesMetadataUpdated', pageMap);

        return this;
    }

    protected createPage(name: string, width: number, height: number, format: ModelMetadata['format'] = null): M {
        const dimension = new Dimension(width, height);

        return this.createModel()
            .setElements([])
            .setBackgroundColor(new BackgroundColor(defaultBackgroundColorParams))
            .setDimensions(dimension)
            .setAssetLoader(this.assetLoader)
            .setTextStyles(this.textStyles) as M;
    }

    public addPage(name: string, width: number, height: number, format: ModelMetadata['format']) {
        const model = this.createPage(name, width, height, format).setup() as M;

        return this.addModel(model, name, width, height, format);
    }

    protected addModel(model: M, name: string, width: number, height: number, format: ModelMetadata['format']) {
        const newPageId = this.generatePageId();

        const metaData: ModelMetadata = {
            id: newPageId,
            name,
            displayOrder: this.models.length + 1,
            format,
            dimensions: { width, height },
        };

        const pageIndex = this.models.push(model) - 1;
        const pageId = metaData.id;

        this.modelsMetaData.push(metaData);

        this.subscribeToModelEvents(model, pageIndex, pageId);

        this.eventEmitter.emit('pageAdded', {
            model,
            videoModel: model, // for backward compatibility
            metaData,
            pageIndex,
            pageId,
        });
        this.setCurrentPageIndex(pageIndex);

        return model;
    }

    removePage(pageIndex: number) {
        if (pageIndex >= this.models.length || pageIndex < 0) {
            throw new Error("There isn't creative model with such index");
        }

        const metaData: ModelMetadata = this.modelsMetaData[pageIndex];
        const model = this.models[pageIndex];

        this.unsubscribeFromModelEvents(model, pageIndex);
        this.modelsMetaData.splice(pageIndex, 1);
        this.models.splice(pageIndex, 1);

        this.eventEmitter.emit('pageRemoved', {
            model,
            videoModel: model,
            metaData,
            pageIndex,
        });

        if (this.index >= pageIndex) {
            let newIndex = this.index - 1;

            if (newIndex < 0 && this.models.length > 0) {
                newIndex = pageIndex;
            }

            this.index = -1;
            this.setCurrentPageIndex(newIndex);
        }

        // Update remaining page indexes after deletion
        const newModelsMeta = [];

        this.models.forEach((model, index) => {
            const modelMeta = this.modelsMetaData[index];
            const newmodelMeta = { ...modelMeta, displayOrder: index + 1 };

            newModelsMeta.push(newmodelMeta);
        });

        if (newModelsMeta) {
            this.setModelsMetaData(newModelsMeta);
        }

        return model;
    }

    renamePage(pageIndex: number, newName: string) {
        if (pageIndex >= this.models.length || pageIndex < 0) {
            throw new Error('There is no creative model exists with this index');
        }

        this.modelsMetaData[pageIndex].name = newName;

        this.eventEmitter.emit('pageRenamed', {
            model: this.models[pageIndex],
            metaData: this.modelsMetaData[pageIndex],
            pageIndex,
        });

        return this;
    }

    updatePageOrder(order: number[], pagesMeta: ModelMetadata[]) {
        const currentPageId = this.getCurrentPageId();

        this.models = updateListOrderByIdxs(this.models, order);
        this.modelsMetaData = pagesMeta;

        const updatedPageIdx = this.modelsMetaData.findIndex((meta) => meta.id === currentPageId);

        if (updatedPageIdx >= 0 && this.getCurrentPageIndex() !== updatedPageIdx) {
            this.setCurrentPageIndex(updatedPageIdx);
        }

        this.eventEmitter.emit('pageOrderChange', { order });

        return this;
    }

    public duplicatePage(
        pageIndex: number,
        params: PageDuplicateParams,
        contentPropertiesSettings: ContentPropertiesSettings,
    ) {
        if (pageIndex >= this.models.length || pageIndex < 0) {
            throw new Error('There is no creative model exists with this index');
        }

        const { model } = this.getPageDuplicate(pageIndex, contentPropertiesSettings);
        model.setup();

        return this.addDuplicatedPage(pageIndex, model, params);
    }

    protected getPageDuplicate(pageIndex: number, contentPropertiesSettings?: ContentPropertiesSettings) {
        const model = this.models[pageIndex]
            .getCopy()
            .setAssetLoader(this.assetLoader)
            .setTextStyles(this.textStyles) as M;

        let id = Date.now() * 2;
        const idMap = new Map();

        model.getAllGlobalProperties().forEach((property: BaseProperty) => {
            const oldId = property.id;
            property.id = ++id;
            idMap.set(oldId, property.id);
        });
        model.getAllElementsRecursively().forEach((element) => {
            const oldId = element.id;
            element.id = ++id;
            idMap.set(oldId, element.id);

            if (
                contentPropertiesSettings &&
                !element.contentPropertyId &&
                matchElementTypeToContentPropertySetting(element, contentPropertiesSettings)
            ) {
                const propertyId = this.contentPropertiesManager.createContentPropertyFromElement(
                    element as ElementForContentProperty,
                    element.name,
                );

                if (propertyId) {
                    this.models[pageIndex].updateElement(oldId, { contentPropertyId: propertyId });
                    element.contentPropertyId = propertyId;
                }
            }
        });
        model.getAllElementsRecursively().forEach((element: ControllableElement) => {
            if ('mask' in element && element.mask) {
                element.mask.elementId = idMap.get(element.mask.elementId);
            }

            if (element instanceof GroupElement && element.activeDropdownElementId !== null) {
                element.activeDropdownElementId = idMap.get(element.activeDropdownElementId) ?? null;
            }
        });

        return { model, idMap };
    }

    protected addDuplicatedPage(pageIndex: number, model: M, params: PageDuplicateParams) {
        const metaData = {
            id: this.generatePageId(),
            name: (params || {}).name || `${this.modelsMetaData[pageIndex].name} Copy`,
            displayOrder: this.models.length + 1,
            dimensions: { width: params.dimensions.width, height: params.dimensions.height },
            format: params.dimensions.format,
        };

        if ((params || {}).dimensions) {
            this.scaleDuplicatedPageModel(model, params.dimensions);
        }

        const duplicatedPageIndex = this.models.push(model) - 1;
        const pageId = metaData.id;

        this.modelsMetaData.push(metaData);
        this.subscribeToModelEvents(model, duplicatedPageIndex, pageId);

        this.getEventEmitter().emit('pageAdded', {
            model,
            metaData,
            pageIndex: duplicatedPageIndex,
            pageId,
        });
        this.setCurrentPageIndex(duplicatedPageIndex);

        return model;
    }

    protected scaleDuplicatedPageModel(
        model: M,
        dimensions: {
            width: number;
            height: number;
        },
    ) {
        const oldDimension = model.getDimensions().getCopy();
        const newDimension = new Dimension(dimensions.width, dimensions.height);

        if (
            oldDimension.getWidth() === newDimension.getWidth() &&
            oldDimension.getHeight() === newDimension.getHeight()
        ) {
            return;
        }

        model.updateDimension(dimensions, { reason: 'user' });

        const scaleFactor = Math.min(
            newDimension.getWidth() / oldDimension.getWidth(),
            newDimension.getHeight() / oldDimension.getHeight(),
        );

        model.getAllElementsRecursively<ControllableElement>('visual').forEach((element) => {
            if (element instanceof TextElement) {
                this.scaleDuplicatedTextElement(model, element, scaleFactor);
            } else if (element instanceof ShapeElement) {
                this.scaleDuplicatedShapeElement(model, element, scaleFactor);
            }
        });
    }

    protected scaleDuplicatedTextElement(model: M, element: TextElement, scaleFactor: number) {
        const formattedText = deepClone(element.formattedText);
        const textBackground = deepClone(element.textBackground?.toObject() ?? null);
        const updatedValues: Partial<TextElement> & {
            formattedText?: TextElement['formattedText'];
            textBackground?: TextElement['textBackground'];
        } = {};

        const oldFontScale = element.fontScale ?? 1;
        const newFontScale = Math.min(
            MAX_TEXT_FONT_SCALE,
            Math.round((element.fontScale ?? 1) * scaleFactor * 100) / 100,
        );

        if (formattedText?.runs && !element.contentPropertyId) {
            formattedText.runs = formattedText.runs.map((run) => ({
                ...run,
                ...(typeof run.paragraphSpacing === 'number'
                    ? { paragraphSpacing: Math.round(run.paragraphSpacing * scaleFactor) }
                    : {}),
                ...(run.stroke && typeof run.stroke.width === 'number'
                    ? {
                          stroke: {
                              ...run.stroke,
                              width: Math.round(run.stroke.width * scaleFactor),
                          },
                      }
                    : {}),
            }));
            updatedValues.formattedText = formattedText;
        }

        if (textBackground) {
            updatedValues.textBackground = {
                ...textBackground,
                spacingTop: Math.round(textBackground.spacingTop * scaleFactor),
                spacingBottom: Math.round(textBackground.spacingBottom * scaleFactor),
                spacingLeft: Math.round(textBackground.spacingLeft * scaleFactor),
                spacingRight: Math.round(textBackground.spacingRight * scaleFactor),
                borderWidth: Math.round((textBackground.borderWidth ?? 0) * scaleFactor),
                cornerRadius: Math.min(
                    MAX_TEXT_BACKGROUND_CORNER_RADIUS,
                    Math.round((textBackground.cornerRadius ?? 0) * scaleFactor),
                ),
            } as TextElement['textBackground'];
        }

        if (oldFontScale !== newFontScale) {
            updatedValues.fontScale = newFontScale;
        }

        if (Object.keys(updatedValues).length) {
            model.updateElement(element.id, updatedValues, {
                walkChildren: false,
                walkParent: false,
                reason: 'user',
            });
        }
    }

    protected scaleDuplicatedShapeElement(model: M, element: ShapeElement, scaleFactor: number) {
        const updatedValues: Partial<ShapeElement> = {};

        if (typeof element.borderWidth === 'number') {
            updatedValues.borderWidth = Math.round((element.borderWidth ?? 0) * scaleFactor);
        }

        if (typeof element.borderRadius === 'number') {
            updatedValues.borderRadius = Math.min(
                MAX_SHAPE_BORDER_RADIUS,
                Math.round((element.borderRadius ?? 0) * scaleFactor),
            );
        }

        if (Object.keys(updatedValues).length) {
            model.updateElement(element.id, updatedValues, {
                walkChildren: false,
                walkParent: false,
                reason: 'user',
            });
        }
    }

    getCompModel(frameIndex = 0) {
        return this.getCurrentModel().getCompModel(frameIndex);
    }

    beginAccumulation(): void {
        this.getCurrentModel().beginAccumulation();
    }

    endAccumulation(reason: Reason = 'user'): void {
        this.getCurrentModel().endAccumulation(reason);
    }

    setElements(elements: ControllableElement[]): this {
        throw new Error('Not Allowed method "setElements" for the MultiPageModel');
    }

    getElements() {
        return this.getCurrentModel().getElements();
    }

    getAllElementsRecursively<T extends ControllableElement>(type: 'all' | 'visual' | 'functional' = 'all'): T[] {
        return this.getCurrentModel().getAllElementsRecursively<T>(type);
    }

    getAllGlobalProperties(): BaseProperty[] {
        return this.getCurrentModel().getAllGlobalProperties() as BaseProperty[];
    }

    getElementById<T extends ControllableElement = ControllableElement>(id: number | string): T | undefined {
        return this.getCurrentModel().getElementById(id) as T | undefined;
    }

    getNextElementSibling(id: number): ControllableElement | null {
        return this.getCurrentModel().getNextElementSibling(id);
    }

    getPrevElementSibling(id: number): ControllableElement | null {
        return this.getCurrentModel().getPrevElementSibling(id);
    }

    getElementsByAssetUsage(asset: IAsset): ControllableElement[] {
        const elements = [];
        this.models.forEach((model) => {
            model.getAllElementsRecursively().forEach((element) => {
                if (element.isContainsAsset(asset)) {
                    elements.push(element);
                }
            });
        });

        return elements;
    }

    setBackgroundColor(backgroundColor: BackgroundColor) {
        this.getCurrentModel().setBackgroundColor(backgroundColor);

        return this;
    }

    getBackgroundColor(): BackgroundColor {
        return this.getCurrentModel().getBackgroundColor();
    }

    setDimensions(dimension: Dimension) {
        this.getCurrentModel().setDimensions(dimension);

        return this;
    }

    getDimensions(): Dimension {
        return this.getCurrentModel().getDimensions();
    }

    getVisibleElements(frameIndex = 0): ControllableElement[] {
        return this.getCurrentModel().getVisibleElements(frameIndex);
    }

    updateElement(elementId: number | string, data: any, options: UpdateElementOptions = {}): void {
        return this.getCurrentModel().updateElement(elementId as number, data, options);
    }

    updateElements(rawElements: any[], options: UpdateElementOptions = {}): void {
        return this.getCurrentModel().updateElements(rawElements, options);
    }

    updateGlobalProperty(
        elType: string,
        rawElement: Partial<BackgroundColorParams> | Partial<AudioElementParams> | Partial<TranscriptElementParams>,
        options: UpdateGlobalPropertyOptions = {},
    ): void {
        return this.getCurrentModel().updateGlobalProperty(elType, rawElement, options);
    }

    updateDimension(
        rawDimension: {
            width: number;
            height: number;
        },
        options: UpdateGlobalPropertyOptions = {},
    ): void {
        return this.getCurrentModel().updateDimension(rawDimension, options);
    }

    createElement(type: ElementTypes, rawParams: any, options: CreateElementOptions): ControllableElement {
        return this.getCurrentModel().createElement(type, rawParams, options);
    }

    addElements(
        elements: ControllableElement[],
        startFrame?: number,
        options: CreateElementOptions = {},
        flattenElementsArray?: ControllableElement[][],
    ): void {
        return this.getCurrentModel().addElements(elements, startFrame, options, flattenElementsArray);
    }

    removeElements(elementIds: number[], options: RemoveElementOptions = {}): void {
        return this.getCurrentModel().removeElements(elementIds, options);
    }

    wrapElementsIntoGroup(elementIds: number[]): GroupElement {
        return this.getCurrentModel().wrapElementsIntoGroup(elementIds);
    }

    unwrapGroupElement(groupId: number | string): void {
        return this.getCurrentModel().unwrapGroupElement(groupId as number);
    }

    addExistingElementToGroup(elementId: number | string, groupId: number | string): GroupElement {
        return this.getCurrentModel().addExistingElementToGroup(elementId as number, groupId as number);
    }

    unwrapElementFromGroup(elementId: number | string): ControllableElement {
        return this.getCurrentModel().unwrapElementFromGroup(elementId);
    }

    getAllMaskElementRanges(): Map<string, Ranges<MaskModeTypes>> {
        return this.getCurrentModel().getAllMaskElementRanges();
    }

    onElementPartialUpdate(elementId: number | string, eventType: ElementUpdateTypes, callbackFn: any): () => void {
        const listener = (data: ElementUpdatedEventData) => {
            const { element, updateTypes } = data;

            if (element.id === elementId && updateTypes.has(eventType)) {
                callbackFn(data);
            }
        };

        this.eventEmitter.on('elementUpdated', listener);

        return () => this.eventEmitter.off('elementUpdated', listener);
    }

    onElementPartialUpdates(elementId: number | string, eventTypes: ElementUpdateTypes[], callbackFn: any): () => void {
        const listener = (data: ElementUpdatedEventData) => {
            const { element, updateTypes } = data;

            if (element.id === elementId && eventTypes.some((eventType) => updateTypes.has(eventType))) {
                callbackFn(data);
            }
        };

        this.eventEmitter.on('elementUpdated', listener);

        return () => this.eventEmitter.off('elementUpdated', listener);
    }

    onElementsPartialUpdate(eventType: ElementUpdateTypes, callbackFn: any): () => void {
        const listener = (data: ElementUpdatedEventData) => {
            const { updateTypes } = data;

            if (updateTypes.has(eventType)) {
                callbackFn(data);
            }
        };

        this.eventEmitter.on('elementUpdated', listener);

        return () => this.eventEmitter.off('elementUpdated', listener);
    }

    onElementUpdate(elementId: number | string, callbackFn: any): () => void {
        const listener = (data: ElementUpdatedEventData) => {
            const { element } = data;

            if (element.id === elementId) {
                callbackFn(data);
            }
        };

        this.eventEmitter.on('elementUpdated', listener);

        return () => this.eventEmitter.off('elementUpdated', listener);
    }

    on(eventName: string, callback: ListenerFn): void {
        this.eventEmitter.on(eventName, callback);
    }

    off(eventName: string, callback: ListenerFn): void {
        this.eventEmitter.off(eventName, callback);
    }

    protected handleTextStylesChanged = (data: TextStyleChangeEventData) => this.syncTextStyleChange(data);

    protected subscribe() {
        this.eventEmitter.on('textStylesChanged', this.handleTextStylesChanged);
    }

    protected unsubscribe() {
        this.eventEmitter.off('textStylesChanged', this.handleTextStylesChanged);
    }

    protected subscribeToModelEvents(model: BaseModel, index: number, id: string): void {
        const listener = (eventName: string, eventData: any = {}) => {
            this.eventEmitter.emit(eventName, eventData);
        };

        this.listeners[index] = listener;
        model.eventEmitter.onAny(listener as ListenerFn);
    }

    protected unsubscribeFromModelEvents(model: BaseModel, index: number): void {
        model.eventEmitter.offAny(this.listeners[index]);
        this.listeners.splice(index, 1);
    }

    protected unsubscribeFromAllModelEvents() {
        this.models.forEach((model, pageIndex) => {
            model.eventEmitter.offAny(this.listeners[pageIndex]);
        });
        this.listeners = [];
    }

    protected syncTextStyleChange({ action, oldValue, newValue, reason }: TextStyleChangeEventData) {
        const update = (model: M) => {
            const updateElements = [];
            const { uuid: styleId, name, ...style } = newValue;

            if (Object.keys(style).length) {
                model.getAllElementsRecursively().forEach((element) => {
                    if (element instanceof TextElement) {
                        const formattedText = TextStyles.updateStyle(element.getTextProps(), styleId, style, oldValue);
                        updateElements.push({ id: element.id, formattedText });
                    }
                });
            }

            if (updateElements.length) {
                model.updateElements(updateElements, { reason });
            }
        };
        const remove = (model: M) => {
            const updateElements = [];
            const styleId = oldValue.uuid;
            model.getAllElementsRecursively().forEach((element) => {
                if (element instanceof TextElement) {
                    const formattedText = TextStyles.removeStyleId(element.getTextProps(), styleId);
                    const propsToChange: any = { id: element.id, formattedText };

                    if (element.textStyles.includes(styleId)) {
                        propsToChange.textStyles = element.textStyles.filter((id) => id !== styleId);
                    }

                    updateElements.push(propsToChange);
                }
            });

            if (updateElements.length) {
                model.updateElements(updateElements, { reason });
            }
        };

        this.models.forEach((model) => {
            if (action === 'remove') {
                remove(model);
            }

            if (action === 'update') {
                update(model);
            }
        });
    }

    protected generatePageId() {
        let id = Date.now();

        while (this.modelsMetaData.some((item) => item.id.toString() === id.toString())) {
            id++;
        }

        return id.toString();
    }

    toObject() {
        return exportSpecificationTemplate(this);
    }

    emit<K extends keyof EventDataMap>(eventName: K, eventData: EventDataMap[K]): void {
        throw new Error('Illegal operation for MultiPageModel');
    }

    getCopy(): BaseMultiPageModel<M> {
        throw new Error('Illegal operation for MultiPageModel');
    }

    copyFrom(model: BaseMultiPageModel<M>): void {
        this.index = model.index;
        this.assetLoader = model.assetLoader;
        this.textStyles = model.textStyles;
        this.contentPropertiesManager = model.contentPropertiesManager.getCopy();
        this.contentPropertiesManager.setEventEmitter(this.eventEmitter);
        this.contentPropertiesManager.setMultiPageModel(this);
        this.models = model.models.map((subModel) => {
            const sub = subModel.getCopy().setAssetLoader(this.assetLoader).setTextStyles(this.textStyles) as M;
            sub.setup();

            return sub;
        });
        this.modelsMetaData = model.modelsMetaData.map((metaData) => {
            const copyMetaData = { ...metaData };

            if (copyMetaData.dimensions) {
                copyMetaData.dimensions = { ...copyMetaData.dimensions };
            }

            return copyMetaData;
        });
    }

    getEventAccumulator(): EventAccumulator {
        throw new Error('Illegal operation for MultiPageModel');
    }

    getUpdateElementOptions(): ElementUpdateOptions {
        throw new Error('Illegal operation for MultiPageModel');
    }

    applySnapshot(snapshot: MultiPageModelSnapshot<M>) {
        const currentPageId = this.modelsMetaData[this.index].id;
        const currentPageModel = this.models[this.index];
        const cratedElements: Element[] = [];

        this.unsubscribeFromAllModelEvents();

        this.modelsMetaData = deepClone(snapshot.modelsMetaData);
        this.models = snapshot.models.map((model) => model.getCopy() as M);

        if (this.contentPropertiesManager && snapshot.contentProperties) {
            this.contentPropertiesManager.loadContentProperties(deepClone(snapshot.contentProperties));
        }

        if (this.modelsMetaData[snapshot.index].id === currentPageId) {
            detectCratedElements(
                currentPageModel.getElements(),
                this.models[snapshot.index].getElements(),
                cratedElements,
            );
        }

        this.models.forEach((model, index) => {
            model.setAssetLoader(this.getAssetLoader());
            model.setTextStyles(this.getTextStyles());
            model.setup();

            this.subscribeToModelEvents(model, index, this.modelsMetaData[index].id);
        });

        this.setCurrentPageIndex(snapshot.index);

        return { cratedElements };
    }

    getSnapshot(): MultiPageModelSnapshot<M> {
        return {
            index: this.index,
            models: this.models.map((model) => model.getCopy() as M),
            modelsMetaData: deepClone(this.modelsMetaData),
            contentProperties: this.contentPropertiesManager
                ? deepClone(this.contentPropertiesManager.allContentProperties)
                : [],
        };
    }
}
